import { useCallback, useEffect, useRef, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL;
const SPEECH_RMS = 0.022;      // energy threshold to count as speech
const BARGE_RMS = 0.06;        // higher bar while the bot is speaking (echo-safe interruption)
const START_MS = 180;          // sustained speech before a segment starts
const SILENCE_MS = 900;        // trailing silence that ends a segment
const MIN_SEG_MS = 500;        // ignore blips
const MAX_SEG_MS = 25000;      // hard cap per utterance

/* Hands-free live voice: keeps the mic open, detects speech energy (VAD), records each utterance,
   sends it to Whisper and hands the transcript back. Supports interrupting the bot (barge-in). */
export const useLiveVoice = ({ sessionId, lang, speaker, busy, onTranscript, onError, onLimit, bargeIn = true }) => {
  const [live, setLive] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle | listening | hearing | thinking | speaking | denied | unsupported
  const [level, setLevel] = useState(0);
  const stream = useRef(null);
  const ctx = useRef(null);
  const rec = useRef(null);
  const raf = useRef(0);
  const seg = useRef({ speechSince: 0, silenceSince: 0, startedAt: 0, chunks: [] });
  const refs = useRef({});
  refs.current = { lang, speaker, busy, onTranscript, onError, onLimit, live };
  const supported = typeof window !== "undefined" && !!(navigator.mediaDevices?.getUserMedia && window.MediaRecorder && (window.AudioContext || window.webkitAudioContext));

  const stopAll = useCallback(() => {
    cancelAnimationFrame(raf.current);
    try { if (rec.current && rec.current.state !== "inactive") { rec.current.onstop = null; rec.current.stop(); } } catch { /* ignore */ }
    rec.current = null;
    stream.current?.getTracks().forEach((t) => t.stop()); stream.current = null;
    ctx.current?.close().catch(() => {}); ctx.current = null;
    seg.current = { speechSince: 0, silenceSince: 0, startedAt: 0, chunks: [] };
    setLevel(0); setLive(false); setPhase("idle");
  }, []);

  const transcribing = useRef(false);
  const transcribe = useCallback(async (blob, durationMs) => {
    transcribing.current = true; setPhase("thinking");
    try {
      const fd = new FormData(); fd.append("file", blob, "clip.webm"); fd.append("session_id", sessionId); fd.append("lang", refs.current.lang || "auto"); fd.append("duration_ms", String(Math.round(durationMs)));
      const r = await fetch(`${API}/api/chat/voice/transcribe`, { method: "POST", body: fd });
      const d = await r.json();
      if (r.status === 403 && d.limit) { refs.current.onLimit?.(d.detail); stopAll(); return; }
      if (!r.ok) throw new Error(d.detail || "Speech recognition failed");
      if (d.text) refs.current.onTranscript?.(d.text, d); else setPhase("listening");
    } catch (ex) { refs.current.onError?.(`${ex.message}. You can type instead.`); setPhase("listening"); }
    finally { transcribing.current = false; }
  }, [sessionId, stopAll]);

  /* Record from the FIRST frame above threshold (no clipped first word); segments shorter than START_MS are discarded as blips. */
  const startSegment = useCallback(() => {
    if (!stream.current || rec.current) return;
    const mime = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"].find((m) => window.MediaRecorder.isTypeSupported?.(m)) || "";
    const mr = new window.MediaRecorder(stream.current, mime ? { mimeType: mime } : undefined);
    seg.current.chunks = []; seg.current.startedAt = performance.now(); seg.current.confirmed = false;
    mr.ondataavailable = (e) => { if (e.data.size) seg.current.chunks.push(e.data); };
    mr.onstop = () => { const dur = performance.now() - seg.current.startedAt; const blob = new Blob(seg.current.chunks, { type: mr.mimeType || "audio/webm" }); rec.current = null; if (seg.current.confirmed && dur >= MIN_SEG_MS && blob.size > 800) transcribe(blob, dur); else setPhase("listening"); };
    mr.start(200); rec.current = mr;
  }, [transcribe]);

  const endSegment = useCallback(() => { try { rec.current?.state !== "inactive" && rec.current?.stop(); } catch { /* ignore */ } }, []);

  const loop = useCallback((analyser, data) => {
    const tick = () => {
      if (!stream.current) return;
      analyser.getByteTimeDomainData(data);
      let sum = 0; for (let i = 0; i < data.length; i++) { const v = (data[i] - 128) / 128; sum += v * v; }
      const rms = Math.sqrt(sum / data.length); setLevel(rms);
      const now = performance.now();
      const { speaker: sp, busy: b } = refs.current;
      const speaking = sp?.speaking;
      const working = b || transcribing.current;
      const threshold = speaking ? (bargeIn ? BARGE_RMS : Infinity) : SPEECH_RMS;
      const s = seg.current;
      if (rms > threshold) {
        s.silenceSince = 0; if (!s.speechSince) s.speechSince = now;
        if (!rec.current && !working) startSegment();
        if (rec.current && !s.confirmed && now - s.speechSince > START_MS) { s.confirmed = true; if (speaking) sp.stop(); setPhase("hearing"); }
      } else {
        s.speechSince = 0; if (rec.current && !s.silenceSince) s.silenceSince = now;
        if (rec.current && s.silenceSince && now - s.silenceSince > (s.confirmed ? SILENCE_MS : 250)) endSegment();
      }
      if (rec.current && now - s.startedAt > MAX_SEG_MS) endSegment();
      if (!rec.current) setPhase((p) => (working ? "thinking" : speaking ? "speaking" : p === "hearing" || p === "thinking" || p === "speaking" ? "listening" : p));
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
  }, [startSegment, endSegment]);

  const start = useCallback(async () => {
    if (!supported) { setPhase("unsupported"); refs.current.onError?.("Your browser doesn't support live voice — please type instead."); return; }
    try {
      const st = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
      stream.current = st;
      const AC = window.AudioContext || window.webkitAudioContext; const ac = new AC(); ctx.current = ac;
      const src = ac.createMediaStreamSource(st); const an = ac.createAnalyser(); an.fftSize = 1024; src.connect(an);
      setLive(true); setPhase("listening"); loop(an, new Uint8Array(an.fftSize));
    } catch (ex) {
      const denied = ex?.name === "NotAllowedError" || ex?.name === "SecurityError";
      setPhase(denied ? "denied" : "idle"); setLive(false);
      refs.current.onError?.(denied ? "Microphone access was blocked. Allow the microphone in your browser's site settings, or simply type your question." : "Could not start the microphone — please type instead.");
    }
  }, [supported, loop]);

  const toggle = useCallback(() => (live ? stopAll() : start()), [live, start, stopAll]);
  useEffect(() => () => stopAll(), [stopAll]);
  return { live, phase, level, toggle, stop: stopAll, supported };
};
